import type { GatewayClient } from './gateway';
import type { ServerToClientMessage } from './types';
import { dispatchPanelChartMessage } from './dispatchers/panelChartDispatcher';
import { dispatchIntegrityMessage } from './dispatchers/integrityDispatcher';
import { dispatchMarketMessage } from './dispatchers/marketDispatcher';
import { dispatchSentimentMessage } from './dispatchers/sentimentDispatcher';
import { dispatchStrategyMessage } from './dispatchers/strategyDispatcher';
import { dispatchGeometryMessage } from './dispatchers/geometryDispatcher';

/**
 * Registers a message listener on the gateway client that dispatches
 * incoming messages to the appropriate Zustand stores.
 *
 * Chart ohlc/signal frames are routed into the per-symbol panelDataStore
 * (dispatchPanelChartMessage) — the sole consumer after the Phase 3b cutover
 * (the legacy global chartStore path was retired). It broadcasts by symbol
 * (no active-symbol gate) so every modular /chart panel ticks live.
 *
 * Returns an unsubscribe function.
 */
export function registerStoreDispatcher(gateway: GatewayClient): () => void {
  return gateway.addListener((msg: ServerToClientMessage) => {
    // Protocol-level messages are handled inside GatewayClient; the dispatcher
    // doesn't need to do anything with them.
    if (isProtocolMessage(msg.type)) return;

    // Per-symbol modular chart data path: routes ohlc/signal frames into
    // panelDataStore. Returns true for any chart-data type it owns so a valid
    // chart frame never trips the DEV "unhandled" warning. Never throws.
    const handled =
      dispatchMarketMessage(msg, gateway) ||
      dispatchIntegrityMessage(msg) ||
      dispatchSentimentMessage(msg) ||
      dispatchStrategyMessage(msg) ||
      dispatchGeometryMessage(msg) ||
      dispatchPanelChartMessage(msg);

    if (!handled && import.meta.env.DEV) {
      console.warn('Unhandled gateway message type in messageDispatcher:', msg.type, msg);
    }
  });
}

const PROTOCOL_MESSAGE_TYPES = new Set([
  'connected',
  'pong',
  'subscribed',
  'unsubscribed',
  'error',
  'command-reply',
]);

function isProtocolMessage(type: string): boolean {
  return PROTOCOL_MESSAGE_TYPES.has(type);
}
